import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getMyPermissions, UserPermissions } from '../utils/auth-api';
import config from '../utils/config';

interface PermissionsContextType {
  permissions: UserPermissions | null;
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  isAdmin: boolean;
  isOwner: boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchPermissions = async () => {
    if (!token || !isAuthenticated) {
      setPermissions(null);
      return;
    }

    try {
      setLoading(true);
      const data = await getMyPermissions(token, config.appId);
      setPermissions(data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [token, isAuthenticated]);

  const hasPermission = (permission: string): boolean => {
    return permissions?.permissions.includes(permission) ?? false;
  };

  const hasAnyPermission = (perms: string[]): boolean => {
    return perms.some(p => hasPermission(p));
  };

  const isAdmin = permissions?.role === 'admin';
  const isOwner = permissions?.role === 'owner';

  const value: PermissionsContextType = {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    isAdmin,
    isOwner,
    refreshPermissions: fetchPermissions,
  };

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};
